import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Clock, CheckCircle2, Circle, ChevronDown, ChevronRight, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import { cardsApi } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { BoardWithLists, BoardCard, UpdateBoardCardRequest } from '@/types'
import { format } from 'date-fns'
import TaskModal from './TaskModal'

interface ListViewProps {
  board: BoardWithLists
  boardId: string
  projectName?: string
  onCardClick: (card: BoardCard) => void
}

const PRIORITY_CONFIG = {
  URGENT: { label: 'Urgent', color: 'bg-red-500', icon: AlertCircle },
  HIGH: { label: 'High', color: 'bg-orange-500', icon: AlertCircle },
  MEDIUM: { label: 'Medium', color: 'bg-yellow-500', icon: Circle },
  LOW: { label: 'Low', color: 'bg-green-500', icon: Circle },
}

export default function ListView({ board, boardId, projectName, onCardClick }: ListViewProps) {
  const queryClient = useQueryClient()
  const [expandedLists, setExpandedLists] = useState<Set<string>>(new Set(board.lists.map(l => l.list_id)))
  const [addingCardToList, setAddingCardToList] = useState<string | null>(null)



  const updateCardMutation = useMutation({
    mutationFn: ({ cardId, data }: { cardId: string; data: UpdateBoardCardRequest }) =>
      cardsApi.update(cardId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['board', boardId] })
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to update task'
      toast.error(errorMessage)
    },
  })

  const handleToggleComplete = (card: BoardCard) => {
    updateCardMutation.mutate({
      cardId: card.card_id,
      data: { is_completed: !card.is_completed }
    })
  }

  const toggleListExpanded = (listId: string) => {
    setExpandedLists(prev => {
      const next = new Set(prev)
      if (next.has(listId)) {
        next.delete(listId)
      } else {
        next.add(listId)
      }
      return next
    })
  }

  const isOverdue = (dueDate: string | undefined, isCompleted: boolean) => {
    if (!dueDate || isCompleted) return false
    return new Date(dueDate) < new Date()
  }

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="bg-card rounded-lg border text-card-foreground">
        <Table>
          <TableHeader>
            <TableRow className="border-border hover:bg-muted/50">
              <TableHead className="w-12"></TableHead>
              <TableHead>Task</TableHead>
              <TableHead className="w-32">Status</TableHead>
              <TableHead className="w-32">Priority</TableHead>
              <TableHead className="w-32">Due Date</TableHead>
              <TableHead className="w-40">Assignees</TableHead>
              <TableHead className="w-24">Labels</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {board.lists.map((list) => (
              <>
                {/* List Header Row */}
                <TableRow
                  key={`list-${list.list_id}`}
                  className="bg-muted/50 hover:bg-muted cursor-pointer border-border"
                  onClick={() => toggleListExpanded(list.list_id)}
                >
                  <TableCell colSpan={7}>
                    <div className="flex items-center gap-2 font-semibold">
                      {expandedLists.has(list.list_id) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                      {list.list_name}
                      <Badge variant="secondary" className="text-xs">
                        {list.cards?.length || 0}
                      </Badge>
                    </div>
                  </TableCell>
                </TableRow>

                {/* Cards in this list */}
                {expandedLists.has(list.list_id) && list.cards?.map((card) => (
                  <TableRow
                    key={card.card_id}
                    className="cursor-pointer hover:bg-muted/50 border-border"
                    onClick={() => onCardClick(card)}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={card.is_completed}
                        onCheckedChange={() => handleToggleComplete(card)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {card.cover_color && (
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: card.cover_color }}
                          />
                        )}
                        <span className={card.is_completed ? 'line-through text-muted-foreground' : ''}>
                          {card.card_title}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={card.is_completed ? 'default' : 'secondary'} className={card.is_completed ? 'bg-green-600 text-white' : ''}>
                        {card.is_completed ? (
                          <><CheckCircle2 className="h-3 w-3 mr-1" /> Done</>
                        ) : (
                          <><Circle className="h-3 w-3 mr-1" /> Open</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {card.priority && PRIORITY_CONFIG[card.priority] && (
                        <Badge
                          variant="outline"
                          className={`${PRIORITY_CONFIG[card.priority].color} text-white border-0`}
                        >
                          {PRIORITY_CONFIG[card.priority].label}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {card.due_date && (
                        <span className={`flex items-center gap-1 text-sm ${
                          isOverdue(card.due_date, card.is_completed) ? 'text-red-500 font-medium' : 'text-muted-foreground'
                        }`}>
                          <Clock className="h-3 w-3" />
                          {format(new Date(card.due_date), 'MMM d, yyyy')}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {card.members && card.members.length > 0 && (
                        <div className="flex -space-x-2">
                          {card.members.slice(0, 3).map((member) => (
                            <div
                              key={member.user_id}
                              className="w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium border-2 border-background"
                              title={member.full_name}
                            >
                              {member.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '?'}
                            </div>
                          ))}
                          {card.members.length > 3 && (
                            <div className="w-6 h-6 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-xs font-medium border-2 border-background">
                              +{card.members.length - 3}
                            </div>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {card.labels && card.labels.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {card.labels.slice(0, 3).map((label) => (
                            <div
                              key={label.label_id}
                              className="h-4 w-4 rounded-full"
                              style={{ backgroundColor: label.label_color }}
                              title={label.label_name || ''}
                            />
                          ))}
                          {card.labels.length > 3 && (
                            <span className="text-xs text-muted-foreground">+{card.labels.length - 3}</span>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Add Card Row */}
                {expandedLists.has(list.list_id) && (
                  <TableRow key={`add-${list.list_id}`} className="border-border">
                    <TableCell colSpan={7}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.stopPropagation()
                          setAddingCardToList(list.list_id)
                        }}
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        Add task
                      </Button>
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Task Modal for creating new cards */}
      {addingCardToList && (
        <TaskModal
          listId={addingCardToList}
          lists={board.lists}
          boardId={boardId}
          projectName={projectName}
          labels={board.labels || []}
          onClose={() => setAddingCardToList(null)}
          mode="create"
        />
      )}
    </div>
  )
}

